import { createSelector } from 'redux-orm';

import orm from '../orm';
import { isLocalId } from '../utils/local-id';
import { selectPath } from './router';
import { calculateCoefficient } from '../utils/calculateCoefficient';
export const makeSelectListById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ List }, id) => {
      const listModel = List.withId(id);

      if (!listModel) {
        return listModel;
      }

      return {
        ...listModel.ref,
        isPersisted: !isLocalId(id),
      };
    },
  );

export const selectListById = makeSelectListById();

export const makeSelectCardIdsByListId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ List }, id) => {
      const listModel = List.withId(id);

      if (!listModel) {
        return listModel;
      }

      return listModel.getFilteredOrderedCardsModelArray().filter(v => v.is_archive != true).map((cardModel) => cardModel.id);
    },
  );

export const selectCardIdsByListId = makeSelectCardIdsByListId();

export const makeSelectArchivedLists = () => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    ({ List, Board }, projectId) => {
      let boardsIds = Board.all().filter(v => v.projectId == projectId).toModelArray().map(v => v.id);
      return List.all().filter(v => (v.is_archive == true && boardsIds.indexOf(v.boardId) > -1)).toModelArray().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });;
    }
  );

export const selectArchivedLists = makeSelectArchivedLists();


export const makeSelectArchivedCards = (state) => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    ({ Card, Board }, projectId) => {
      let boardsIds = Board.all().filter(v => v.projectId == projectId).toModelArray().map(v => v.id);
      return Card.all().filter(v => (v.is_archive == true && boardsIds.indexOf(v.boardId) > -1)).toModelArray().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });
    }
  );

export const selectArchivedCards = makeSelectArchivedCards();

export const makeSelectArchivedBoards = () => 
  createSelector(
    orm,
    (state) => selectPath(state).projectId,
    ({ Board }, id) => {
      return Board.all().filter(v => v.is_archive == true && v.projectId == id).toModelArray().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((v) => { 
        const date = new Date(v.updatedAt);
        date.setHours(date.getHours() + 3);
        return {...v.ref, updatedAt: date.toISOString().slice(0, 16).replace('T', ' ')} 
      });;
    }
  );

export const selectArchivedBoards = makeSelectArchivedBoards();

export const makeSelectListPointsSum = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    ({ List, Card, CardPointData, CardPointAdvancedData, ContentCardDefault, ContentViewPlan, DampingCoefficient }, listId, pointSystemId) => {
      if (!listId || !pointSystemId) {
        return 0;
      }

      const listModel = List.withId(listId);
      if (!listModel) {
        return 0;
      }

      // Получаем текущий месяц и год
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12

      // Получаем ContentViewPlan для текущего месяца/года
      const contentViewPlanModel = ContentViewPlan.all()
        .filter({
          pointSistemId: pointSystemId,
          year: currentYear,
          month: currentMonth,
        })
        .first();
      const contentViewPlan = contentViewPlanModel?.ref || null;

      // Получаем DampingCoefficients для данной системы баллов
      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .orderBy('id')
        .toRefArray();

      const cardModels = listModel.getFilteredOrderedCardsModelArray().filter(v => v.is_archive != true);
      let sum = 0;

      cardModels.forEach((cardModel) => {
        const cardId = cardModel.id;

        // Получаем CardPointData для карточки
        const cardPointDataModel = CardPointData.all()
          .filter({ cardId, pointSistemId: pointSystemId })
          .first();

        if (!cardPointDataModel) {
          return;
        }

        const cardPointData = cardPointDataModel.ref;

        // Получаем ContentCardDefault через ccdId
        let contentCardDefault = null;
        if (cardPointDataModel.ccdId) {
          const contentCardDefaultModel = ContentCardDefault.withId(cardPointDataModel.ccdId);
          // Проверяем, что ContentCardDefault существует и принадлежит правильному pointSystemId
          if (
            contentCardDefaultModel &&
            contentCardDefaultModel.pointSistemId === pointSystemId
          ) {
            contentCardDefault = contentCardDefaultModel.ref;
          }
        }

        // Рассчитываем коэффициент
        let coefficient = 1;
        if (contentCardDefault && cardPointData && dampingCoefficientModels.length > 0) {
          coefficient = calculateCoefficient({
            contentCardDefault,
            cardPointData,
            contentViewPlan,
            dampingCoefficients: dampingCoefficientModels,
          });
        }

        // Считаем базовые баллы
        let basePoints = 0;
        if (contentCardDefault && contentCardDefault.value) {
          basePoints = contentCardDefault.value;
        }

        // Получаем все CardPointAdvancedData для карточки и суммируем их value
        const cardPointAdvancedDataModels = CardPointAdvancedData.all()
          .filter({ cardId, pointSistemId: pointSystemId })
          .toModelArray();

        let extraPoints = 0;
        cardPointAdvancedDataModels.forEach((advancedDataModel) => {
          if (advancedDataModel.value) {
            extraPoints += advancedDataModel.value || 0;
          }
        });

        // Применяем коэффициент: totalPoints = (basePoints + extraPoints) * coefficient
        const totalPoints = (basePoints + extraPoints) * coefficient;
        sum += totalPoints;
      });

      return sum;
    },
  );

export const selectListPointsSum = makeSelectListPointsSum();

export default {
  makeSelectListById,
  selectListById,
  selectArchivedLists,
  makeSelectArchivedLists,
  selectArchivedCards,
  makeSelectArchivedCards,
  selectArchivedBoards,
  makeSelectArchivedBoards,
  makeSelectCardIdsByListId,
  selectCardIdsByListId,
  makeSelectListPointsSum,
  selectListPointsSum,

};
